import { Empty, PriorityBadge, useFormat } from '@ly360/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { CRM_KEYS, type TaskRow } from './data';

/** Task rows with a one-click complete toggle; overdue due dates are highlighted. */
export function TaskList({ items, showRelated = true }: { items: TaskRow[]; showRelated?: boolean }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (task: TaskRow) =>
      api(`/v1/tasks/${task.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: task.status === 'done' ? 'open' : 'done' }),
      }),
    onSuccess: () => CRM_KEYS.forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
  if (!items.length) return <Empty text={t('crm.tasks.empty')} />;
  const now = Date.now();
  return (
    <ul className="list-plain" data-testid="task-list">
      {items.map((task) => {
        const overdue = task.status === 'open' && task.dueAt && new Date(task.dueAt).getTime() < now;
        return (
          <li key={task.id}>
            <button
              className={`check-btn${task.status === 'done' ? ' done' : ''}`}
              aria-label={task.status === 'done' ? t('crm.tasks.reopen') : t('crm.tasks.complete')}
              title={task.status === 'done' ? t('crm.tasks.reopen') : t('crm.tasks.complete')}
              onClick={() => toggle.mutate(task)}
            >
              <Check size={13} />
            </button>
            <div className="grow">
              <div style={{ textDecoration: task.status === 'done' ? 'line-through' : undefined }} dir="auto">
                {task.title}
              </div>
              <div className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                <span className={overdue ? 'overdue' : undefined}>
                  {task.dueAt ? fmt.dateTime(task.dueAt) : t('crm.tasks.noDue')}
                </span>
                {showRelated && task.relatedLabel && <> · {task.relatedLabel}</>}
                {task.assigneeName && <> · {task.assigneeName}</>}
              </div>
            </div>
            {task.priority !== 'normal' && <PriorityBadge value={task.priority} />}
          </li>
        );
      })}
    </ul>
  );
}
